import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { PresentUrl, submitotpSendApi, submitotpVerifyApi, userSignApi } from "../../../text/apidata.js";
import { responsiveToast } from "../../AppComponent/responsiveToast.js";
import styles from "./BasicInformation.module.scss";

export default function SubmissionForm({
  phoneNumber = "",
  timelineId = "",
  sessionToken = "",
  onSuccess = () => { },
  showToast = () => { },
}) {
  const [mobileNum, setMobileNum] = useState(phoneNumber);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [submittingSign, setSubmittingSign] = useState(false);

  const canvasRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const isDrawingRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (phoneNumber && !mobileNum) {
      setMobileNum(phoneNumber);
    }
  }, [phoneNumber, mobileNum]);

  // Handle responsive canvas sizing & drawing context
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvasContainerRef.current;
    if (!canvas || !container) return;

    const updateCanvasDimensions = () => {
      const containerWidth = Math.min(container.clientWidth || 500, 600);
      if (canvas.width !== containerWidth) {
        canvas.width = containerWidth;
        canvas.height = 180;
        const ctx = canvas.getContext("2d");
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#0f172a";
      }
    };

    updateCanvasDimensions();
    window.addEventListener("resize", updateCanvasDimensions);
    return () => window.removeEventListener("resize", updateCanvasDimensions);
  }, [otpVerified]);

  const getHeaders = () => {
    if (!sessionToken) return {};
    return {
      Authorization: String(sessionToken).startsWith("Bearer ")
        ? sessionToken
        : `Bearer ${sessionToken}`,
    };
  };

  const handleSendOtp = async () => {
    if (!mobileNum || mobileNum.length !== 10) {
      showToast("Please enter a valid 10-digit phone number");
      return;
    }

    setSendingOtp(true);
    try {
      const res = await axios.post(
        submitotpSendApi,
        {
          phone_number: mobileNum,
        },
        { headers: getHeaders() }
      );

      console.log("Send Submit OTP response:", res.data);
      setOtpSent(true);
      const msg = res.data?.message || res.data?.status?.message || "OTP sent successfully";
      // showToast(msg);
      responsiveToast(msg, "success");
    } catch (err) {
      console.error("Error sending submit OTP:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Failed to send OTP. Please try again.";
      showToast(errMsg);
      responsiveToast(errMsg, "error");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOtpDigitChange = (e, index) => {
    const val = e.target.value.replace(/\D/g, "");
    const newOtp = [...otpDigits];
    newOtp[index] = val.slice(-1);
    setOtpDigits(newOtp);

    if (val && index < 5) {
      const nextInput = document.getElementById(`submit-otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`submit-otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const otpValue = otpDigits.join("");

  const handleVerifyOtp = async () => {
    if (!otpValue || otpValue.length < 4) {
      showToast("Please enter a valid OTP");
      return;
    }

    setVerifyingOtp(true);
    try {
      const res = await axios.post(
        submitotpVerifyApi,
        {
          phone_number: mobileNum,
          otp: otpValue,
        },
        { headers: getHeaders() }
      );

      console.log("Verify Submit OTP response:", res.data);
      const statusMsg = String(res.data?.status?.message || res.data?.message || "").toLowerCase();
      const isSuccess =
        res.data?.status?.code === 200 ||
        statusMsg.includes("verified") ||
        statusMsg.includes("success") ||
        res.data?.code === 200;

      if (isSuccess) {
        setOtpVerified(true);
        const msg = "OTP verified successfully!";
        showToast(msg);
        responsiveToast(msg, "success");
      } else {
        const errMsg = res.data?.message || "Invalid OTP. Please try again.";
        showToast(errMsg);
        responsiveToast(errMsg, "error");
      }
    } catch (err) {
      console.error("Error verifying submit OTP:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Invalid OTP. Please try again.";
      showToast(errMsg);
      responsiveToast(errMsg, "error");
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Mobile Touch & Mouse Drawing Handlers
  const startDrawing = (e) => {
    if (!otpVerified) return;
    if (e.touches && e.cancelable) e.preventDefault();
    isDrawingRef.current = true;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    ctx.beginPath();
    ctx.moveTo(clientX - rect.left, clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawingRef.current || !otpVerified) return;
    if (e.touches && e.cancelable) e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    ctx.lineTo(clientX - rect.left, clientY - rect.top);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSubmitForm = async () => {
    if (!otpVerified) {
      showToast("Please verify OTP before submitting");
      return;
    }

    if (!hasSignature) {
      showToast("Please draw your signature in the signature pad");
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    setSubmittingSign(true);

    try {
      // 1. Convert Canvas to Blob File
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
      if (!blob) throw new Error("Failed to capture signature image");

      const file = new File([blob], "signature.png", { type: "image/png" });
      const filename = `onboarding/user_sign/signature_${Math.floor(100000 + Math.random() * 900000)}.png`;

      // 2. Request Presigned URL
      const presignedRes = await axios.post(
        PresentUrl,
        {
          multiple_files: [
            {
              filename: filename,
              file_type: "image/png",
            },
          ],
        },
        { headers: getHeaders() }
      );

      console.log("Signature Presigned URL response:", presignedRes.data);
      const urlList =
        presignedRes.data?.presigned_url ||
        presignedRes.data?.data?.presigned_url ||
        presignedRes.data?.data ||
        presignedRes.data ||
        [];

      const firstItem = Array.isArray(urlList) ? urlList[0] : urlList;
      const presignedPutUrl = typeof firstItem === "string" ? firstItem : firstItem?.presigned_url || firstItem?.url;

      if (!presignedPutUrl) {
        throw new Error("Failed to obtain presigned upload URL");
      }

      // 3. Upload File to S3
      await fetch(presignedPutUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "image/png",
          "x-amz-acl": "public-read",
        },
        body: file,
      });

      const finalSignS3Url = presignedPutUrl.split("?")[0];
      console.log("Uploaded Signature S3 URL:", finalSignS3Url);

      // 4. Submit Final Signature Payload
      const signPayload = {
        sign: finalSignS3Url,
      };

      const signRes = timelineId
        ? await axios.put(`${userSignApi}${timelineId}/`, signPayload, { headers: getHeaders() })
        : await axios.post(userSignApi, signPayload, { headers: getHeaders() });

      console.log("User Sign API response:", signRes.data);
      showToast("Form submitted successfully!");
      responsiveToast("Form submitted successfully!", "success");
      onSuccess();
    } catch (err) {
      console.error("Error submitting final signature:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.detail ||
        "Failed to submit signature. Please try again.";
      showToast(errMsg);
      responsiveToast(errMsg, "error");
    } finally {
      setSubmittingSign(false);
    }
  };

  return (
    <div className={styles.basicInformation}>
      <header className={styles.sectionHeader}>Final Form Submission</header>

      <div className={styles.content} style={{ padding: "16px" }}>
        {/* Section 1: Phone & OTP Verification */}
        <div style={{ marginBottom: "24px", padding: "16px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <h3 style={{ fontSize: "16px", fontWeight: "700", marginBottom: "16px", color: "#0f172a" }}>
            Step 1: Verify Registered Mobile Number
          </h3>

          <div style={{ display: "flex", gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
            <label className={styles.field} style={{ flex: "1 1 220px", minWidth: "220px", margin: 0 }}>
              <span>Phone Number<b>*</b></span>
              <input
                value={mobileNum}
                maxLength={10}
                inputMode="numeric"
                onChange={(e) => setMobileNum(e.target.value.replace(/\D/g, ""))}
                placeholder="Enter 10-digit phone number"
                disabled={true}
                style={{ width: "100%", height: "42px" }}
              />
            </label>

            {!otpVerified && (
              <button
                type="button"
                className={styles.uploadButton}
                onClick={handleSendOtp}
                disabled={sendingOtp || !mobileNum || mobileNum.length !== 10}
                style={{ height: "42px", minWidth: "110px", flexShrink: 0 }}
              >
                {sendingOtp ? "Sending..." : otpSent ? "Resend OTP" : "Send OTP"}
              </button>
            )}

            {otpVerified && (
              <span style={{ height: "42px", display: "inline-flex", alignItems: "center", color: "#16a34a", fontWeight: "700", fontSize: "15px" }}>
                ✓ OTP Verified
              </span>
            )}
          </div>

          {/* OTP Digit Input Box */}
          {otpSent && !otpVerified && (
            <div style={{ marginTop: "20px" }}>
              <label className={styles.label}>Enter 6-Digit OTP<span>*</span></label>
              <div style={{ display: "flex", gap: "10px", marginTop: "8px", alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      id={`submit-otp-input-${index}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpDigitChange(e, index)}
                      onKeyDown={(e) => handleOtpKeyDown(e, index)}
                      style={{
                        width: "38px",
                        height: "42px",
                        textAlign: "center",
                        fontSize: "18px",
                        fontWeight: "700",
                        borderRadius: "6px",
                        border: "1.5px solid #cbd5e1",
                        background: "#ffffff",
                      }}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.uploadButton}
                  onClick={handleVerifyOtp}
                  disabled={verifyingOtp || otpValue.length < 4}
                  style={{ height: "42px", minWidth: "110px" }}
                >
                  {verifyingOtp ? "Verifying..." : "Verify OTP"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Signature Pad */}
        <div style={{ padding: "16px", background: "#ffffff", borderRadius: "8px", border: "1.5px solid #cbd5e1", opacity: otpVerified ? 1 : 0.6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", flexWrap: "wrap", gap: "8px" }}>
            <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: 0 }}>
              Step 2: Draw Your Digital Signature
            </h3>
            {hasSignature && (
              <button
                type="button"
                className={styles.removeButton}
                onClick={clearSignature}
                style={{ fontSize: "13px", height: "34px", padding: "0 12px" }}
              >
                Clear Signature
              </button>
            )}
          </div>

          {!otpVerified && (
            <p style={{ color: "#ef4444", fontSize: "14px", fontWeight: "600", marginBottom: "12px" }}>
              ⚠️ Please verify OTP above to enable the signature pad.
            </p>
          )}

          <div
            ref={canvasContainerRef}
            style={{
              width: "100%",
              maxWidth: "600px",
              border: "2px dashed #94a3b8",
              borderRadius: "8px",
              overflow: "hidden",
              background: "#f8fafc",
              touchAction: "none",
            }}
          >
            <canvas
              ref={canvasRef}
              width={500}
              height={180}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              style={{
                cursor: otpVerified ? "crosshair" : "not-allowed",
                display: "block",
                width: "100%",
                height: "180px",
                touchAction: "none",
              }}
            />
          </div>

          <div style={{ marginTop: "20px" }}>
            <button
              type="button"
              className={styles.activeButton}
              onClick={handleSubmitForm}
              disabled={submittingSign || !otpVerified || !hasSignature}
              style={{
                width: "100%",
                maxWidth: "320px",
                height: "46px",
                fontSize: "16px",
                fontWeight: "700",
                opacity: submittingSign || !otpVerified || !hasSignature ? 0.6 : 1,
              }}
            >
              {submittingSign ? "Submitting Form..." : "Submit Onboarding Form"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
